// src/lib/useDoctors.ts
// 疾患深掘りタブ サイドバー の 医師 selector 用 hook。
// /api/query/doctors?dateFrom=...&dateTo=...&dep=...&granularity=... を呼んで
// 期間 + 診療科フォーカス連動の症例数つき医師リストを取得。
// - dep (focusedDept) 指定時: その診療科で実績ある (n_cases > 0) 医師のみ返る
// - dep 未指定時: 全医師 (n_cases 0 含む) を返す (後方互換)
import { useQuery } from "@tanstack/react-query";
import { useFilters } from "../store";
import { useAuth } from "./useAuth";
import { diseasePeriod } from "./useDiseaseRanking";
import type { DoctorListResponse } from "@shared/api";
import { GP_MODE_HEADER } from "./gpMode";

export function useDoctors() {
  const f = useFilters();
  // Codex 指摘 [High]: canViewDoctor=false の user では fetch 自体を行わない
  // (UI 隠蔽だけでは Network タブに 医師情報 が 残ってしまうため)
  const { canViewDoctor } = useAuth();
  // 期間: 疾患深掘りタブと同じ計算ルート (asOfMonth 起点 直近12ヶ月、 override 優先)
  const { dateFrom: defFrom, dateTo: defTo } = diseasePeriod(f.asOfMonth, f.dpcfy, f.diseaseAutoPeriod);
  const dateFrom = f.tab === "入院実績" ? f.periodFrom : f.dateFromOverride ?? defFrom;
  const dateTo   = f.tab === "入院実績" ? f.asOfMonth : f.dateToOverride ?? defTo;
  const dep = f.focusedDept;
  const granularity = f.granularity;

  return useQuery({
    queryKey: ["doctors", dateFrom, dateTo, dep, granularity],
    enabled: canViewDoctor,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("dateFrom", dateFrom);
      params.set("dateTo", dateTo);
      if (dep) params.set("dep", dep);
      params.set("granularity", granularity);
      const r = await fetch(`/api/query/doctors?${params.toString()}`, {
        credentials: "include",
        headers: { ...GP_MODE_HEADER },
      });
      if (!r.ok) throw new Error(`doctors ${r.status}`);
      return r.json() as Promise<DoctorListResponse>;
    },
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });
}
